// eslint-disable-next-line no-unused-vars
import React, { memo, useRef, useState, useEffect, useCallback } from 'react';
import { array, bool, func, string } from 'prop-types';
import useSWR from 'swr';

import OrderProductsModalComponent from 'Components/Order/OrderProductsModal';
import ModalManager from 'Containers/ModalManager/ModalManager';
import { API_PRODUCTS_SEARCH, CURRENCY_RON } from 'Constants';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import useAnalytics from 'Hooks/useAnalytics';

const defaultState = {
  products: []
};

const OrderProductsModal = memo(({
  open,
  onClose,
  onAddProduct,
  title,
  addButtonLabel,
  trackingLocation,
  searchAnalyticsEvent,
  buttonAnalyticsEvent,
  requiresAddToCartPermission,
  excludedProductIds,
  allowPriceRequestProducts,
  allowOutOfStock,
  includeSearchFilters
}) => {
  const searchRef = useRef(null);
  const fetch = useFetch();
  const analytics = useAnalytics();
  const { data: user } = useUser();
  const { isExternalReseller: IS_EXTERNAL_RESELLER } = usePermissions();
  const defaultCurrency = IS_EXTERNAL_RESELLER ? user?.defaultCurrency : CURRENCY_RON;
  // const [onlyAvailable, setOnlyAvailable] = useState(parseInt(query?.only_available, 10) === 1);
  const [searchResults, setSearchResults] = useState(defaultState);
  const [currentSearch, setSearch] = useState('');
  const { data, isValidating: isLoading, error } = useSWR(
    () => {
      if (!open) {
        return null;
      }
      const search = currentSearch.length >= 3 ? currentSearch : '';
      if (!includeSearchFilters) {
        return search
          ? `${ API_PRODUCTS_SEARCH }?search=${ encodeURIComponent(search) }`
          : API_PRODUCTS_SEARCH;
      }
      const baseUrl = `${API_PRODUCTS_SEARCH}?only_available=${ allowOutOfStock ? 0 : 1 }&stock_clearing_sale=0&including_resealed=0&currency=ron&vat=false`;
      return `${baseUrl}&search=${encodeURIComponent(search)}`;
    },
    fetch,
    {
      revalidateOnMount: true,
    }
  );

  useEffect(() => {
    if (data) {
      if (currentSearch.length >= 3) {
        analytics.track(searchAnalyticsEvent, { search: currentSearch });
      }
      const excludedIds = new Set(excludedProductIds);
      const filteredProducts = data?.products?.filter(product => (
        !excludedIds.has(product.productId || product.id)
        && (allowPriceRequestProducts || product?.hasPriceRequest === false)
      ));
      const newData = { ...data, products: filteredProducts };
      if (defaultState.products.length === 0) {
        Object.assign(defaultState, newData);
      }
      setSearchResults(newData);
    }
  }, [data, excludedProductIds, allowPriceRequestProducts]);

  // const toggleOnlyAvailable = useCallback(
  //   checked => {
  //     setOnlyAvailable(prevState => {
  //       if (checked !== prevState) {
  //         debouncedProductFetch(currentSearch.trim(), checked);
  //         return checked;
  //       }
  //       return prevState;
  //     });
  //   },
  //   [currentSearch, debouncedProductFetch]
  // );

  const handleOnChange = useCallback(
    evt => {
      if (evt?.preventDefault) {
        evt.preventDefault();
      }
      const inputTextValue = evt?.target?.value;
      setSearch(prevSearch => {
        if (inputTextValue !== prevSearch) {
          return inputTextValue || '';
        }
        return prevSearch;
      });
    },
    []
  );

  useEffect(() => {
    let timeoutId;

    if (open) {
      timeoutId = setTimeout(() => {
        searchRef?.current?.input?.focus();
      }, 100);
    } else {
      timeoutId = setTimeout(() => {
        setSearch('');
        setSearchResults(defaultState);
      }, 250);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [open]);

  return (
    <ModalManager open={ open }>
      <OrderProductsModalComponent
        searchRef={ searchRef }
        currency={ defaultCurrency }
        onClose={ onClose }
        onChange={ handleOnChange }
        // onToggleAvailable={ toggleOnlyAvailable }
        isValid={ !currentSearch?.length || currentSearch?.length >= 3 }
        onAddToOrder={ onAddProduct }
        search={ currentSearch }
        searchResults={ searchResults }
        error={ error?.message || error }
        // onlyAvailable={ onlyAvailable }
        isLoading={ isLoading }
        isExternalReseller={ IS_EXTERNAL_RESELLER }
        title={ title }
        addButtonLabel={ addButtonLabel }
        trackingLocation={ trackingLocation }
        buttonAnalyticsEvent={ buttonAnalyticsEvent }
        requiresAddToCartPermission={ requiresAddToCartPermission }
        allowOutOfStock={ allowOutOfStock }
      />
    </ModalManager>
  );
});

OrderProductsModal.propTypes = {
  open: bool,
  onClose: func,
  onAddProduct: func,
  title: string,
  addButtonLabel: string,
  trackingLocation: string,
  searchAnalyticsEvent: string,
  buttonAnalyticsEvent: string,
  requiresAddToCartPermission: bool,
  excludedProductIds: array,
  allowPriceRequestProducts: bool,
  allowOutOfStock: bool,
  includeSearchFilters: bool
};

OrderProductsModal.defaultProps = {
  excludedProductIds: [],
  allowPriceRequestProducts: false,
  allowOutOfStock: false,
  includeSearchFilters: true,
  searchAnalyticsEvent: 'reservation_modal_search',
  buttonAnalyticsEvent: 'add_to_cart',
  requiresAddToCartPermission: true
};
OrderProductsModal.displayName = 'OrderProductsModalContainer';

export default OrderProductsModal;
